import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { UnifiedServiceReportService } from '../../../../services/unified-service-report.service';
import { UnifiedServiceReportData, UnifiedServiceReportFilter, RecurringServiceType } from '../../../../models/dtos/unified-service-report';
import { PaginationFilter } from '../../../../models/pagination-filter';
import moment from 'moment';
import { StorageUtil } from '../../../../utils/storage.util';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-unified-service-report',
  templateUrl: './unified-service-report.component.html',
  styleUrls: ['./unified-service-report.component.scss']
})
export class UnifiedServiceReportComponent implements OnInit, OnDestroy {
  filterForm: FormGroup;
  reportData: UnifiedServiceReportData[] = [];
  filteredReportData: UnifiedServiceReportData[] = [];
  loading = false;
  totalElements = 0;
  pageSize = 10;
  currentPage = 0;
  totalValue = 0;
  recurringServiceTypeOptions: { label: string, value: string }[] = [];
  serviceNameOptions: { label: string, value: string }[] = [];
  serviceStatusOptions: { label: string, value: any }[] = [
    { label: 'Ambos', value: null },
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false }
  ];
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private unifiedServiceReportService: UnifiedServiceReportService
  ) {
    this.filterForm = this.fb.group({
      customerName: [''],
      codRegistration: [''],
      serviceName: [''],
      serviceStartDate: [null],
      serviceEndDate: [null],
      recurringServiceType: [[] as RecurringServiceType[]],
      serviceStatus: [null]
    });
  }

  ngOnInit(): void {
    this.loadServiceTypes();
    this.loadRecurringServiceTypes();
    this.loadAllData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadServiceTypes(): void {
    this.unifiedServiceReportService.getDistinctServiceTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (serviceTypes) => {
          this.serviceNameOptions = [
            { label: 'Todos', value: '' },
            ...serviceTypes.map(type => ({ label: type, value: type }))
          ];
        },
        error: (error) => {
          console.error('Erro ao carregar tipos de serviço:', error);
          this.serviceNameOptions = [{ label: 'Todos', value: '' }];
        }
      });
  }

  loadRecurringServiceTypes(): void {
    this.unifiedServiceReportService.getDistinctServiceTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (serviceTypes) => {
          this.recurringServiceTypeOptions = [
            { label: 'Todos', value: '' },
            ...serviceTypes.map(type => ({ label: type, value: type }))
          ];
        },
        error: (error) => {
          console.error('Erro ao carregar tipos de serviço recorrente:', error);
          this.recurringServiceTypeOptions = [{ label: 'Todos', value: '' }];
        }
      });
  }

  loadData(): void {
    this.loading = true;
    
    const formValues = this.filterForm.value;
    const filters: UnifiedServiceReportFilter = {
      customerName: formValues.customerName || '',
      codRegistration: formValues.codRegistration || '',
      serviceName: formValues.serviceName || '',
      serviceStartDate: formValues.serviceStartDate ? this.formatDate(formValues.serviceStartDate) : undefined,
      serviceEndDate: formValues.serviceEndDate ? this.formatDate(formValues.serviceEndDate) : undefined,
      recurringServiceType: formValues.recurringServiceType && formValues.recurringServiceType.length > 0 ? formValues.recurringServiceType as RecurringServiceType[] : undefined,
      active: formValues.serviceStatus
    };
    
    const paginationFilter: PaginationFilter = {
      page: this.currentPage,
      size: this.pageSize,
      sort: 'id',
      order: 'ASC'
    };

    this.unifiedServiceReportService.getUnifiedServiceReport(filters, paginationFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.reportData = response.content;
          this.filteredReportData = [...this.reportData];
          console.log('unified', this.reportData);
          
          this.totalElements = response.totalElements || 0;
          this.totalValue = Number((response.totalElementsValues ?? 0).toFixed(2));
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar dados do relatório:', error);
          this.reportData = [];
          this.filteredReportData = [];
          this.totalElements = 0;
          this.totalValue = 0;
          this.loading = false;
        }
      });
  }

  loadAllData(): void {
    this.loading = true;
    
    const filters: UnifiedServiceReportFilter = {
      customerName: '',
      codRegistration: '',
      serviceName: '',
      serviceStartDate: undefined,
      serviceEndDate: undefined,
      recurringServiceType: [] as RecurringServiceType[],
      active: null
    };
    
    const paginationFilter: PaginationFilter = {
      page: this.currentPage,
      size: this.pageSize,
      sort: 'id',
      order: 'ASC'
    };

    this.unifiedServiceReportService.getUnifiedServiceReport(filters, paginationFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.reportData = response.content;
          console.log('unified all', this.reportData);
          
          this.filteredReportData = [...this.reportData];
          this.totalElements = response.totalElements || 0;
          this.totalValue = Number((response.totalElementsValues ?? 0).toFixed(2));
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar dados do relatório:', error);
          this.reportData = [];
          this.filteredReportData = [];
          this.totalElements = 0;
          this.totalValue = 0;
          this.loading = false;
        }
      });
  }

  searchData(): void {
    this.currentPage = 0;
    this.loadData();
  }

  onLazyLoad(event: LazyLoadEvent): void {
    this.currentPage = Math.floor((event.first || 0) / (event.rows || 10));
    this.pageSize = event.rows || 10;
    this.loadData();
  }

  async exportExcel(): Promise<void> {
    if (this.loading) {
      return;
    }

    const formValues = this.filterForm.value;
    const filters: UnifiedServiceReportFilter = {
      customerName: formValues.customerName || '',
      codRegistration: formValues.codRegistration || '',
      serviceName: formValues.serviceName || '',
      serviceStartDate: formValues.serviceStartDate ? this.formatDate(formValues.serviceStartDate) : undefined,
      serviceEndDate: formValues.serviceEndDate ? this.formatDate(formValues.serviceEndDate) : undefined,
      recurringServiceType: formValues.recurringServiceType && formValues.recurringServiceType.length > 0 ? formValues.recurringServiceType as RecurringServiceType[] : undefined,
      active: formValues.serviceStatus
    };

    const sizeForExport = this.totalElements && this.totalElements > 0 ? this.totalElements : 100000;

    const paginationFilter: PaginationFilter = {
      page: 0,
      size: sizeForExport,
      sort: 'id',
      order: 'ASC'
    };

    this.loading = true;

    this.unifiedServiceReportService.getUnifiedServiceReport(filters, paginationFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          import('xlsx').then(xlsx => {
            const exportData = (response.content || []).map(item => {
              const itemAny = item as any;
              return {
                'Número do Título': item.titleNumber,
                'Cliente': item.customerName,
                'Tipo de Origem': item.originType === 'BOAT' ? 'Embarcação' : 'Cliente',
                'Nome da Embarcação': item.originType === 'BOAT' ? item.boatName : '',
                'Serviço': item.serviceName,
                'Observações': itemAny.observation || '',
                'Referência da Caixa': itemAny.boxRef || '',
                'Status do Serviço': item.serviceStatus,
                'Início do Contrato': item.contractStartDate ? moment(item.contractStartDate).format('DD/MM/YYYY') : '',
                'Fim do Contrato': item.contractEndDate ? moment(item.contractEndDate).format('DD/MM/YYYY') : '',
                'Valor Total': item.totalValue
              };
            });

            exportData.push({} as any);
            exportData.push({
              'Número do Título': 'Total Geral',
              'Cliente': '',
              'Tipo de Origem': '',
              'Nome da Embarcação': '',
              'Serviço': '',
              'Observações': '',
              'Referência da Caixa': '',
              'Status do Serviço': '',
              'Início do Contrato': '',
              'Fim do Contrato': '',
              'Valor Total': Number((response.totalElementsValues ?? 0).toFixed(2))
            });

            const worksheet = xlsx.utils.json_to_sheet(exportData);

            const columnWidths = [
              { wch: 15 }, // Número do Título
              { wch: 30 }, // Cliente
              { wch: 15 }, // Tipo de Origem
              { wch: 25 }, // Nome da Embarcação
              { wch: 25 }, // Serviço
              { wch: 40 }, // Observações
              { wch: 30 }, // Referência da Caixa
              { wch: 15 }, // Status do Serviço
              { wch: 15 }, // Início do Contrato
              { wch: 15 }, // Fim do Contrato
              { wch: 15 }  // Valor Total
            ];

            worksheet['!cols'] = columnWidths;

            const totalRowIndex = exportData.length; // Índice da linha de Total Geral
            const range = xlsx.utils.decode_range(worksheet['!ref']);

            for (let C = range.s.c; C <= range.e.c; C++) {
              const cellRef = xlsx.utils.encode_cell({ r: totalRowIndex - 1, c: C });
              if (!worksheet[cellRef]) worksheet[cellRef] = { t: 's', v: '' };
              worksheet[cellRef].s = { font: { bold: true } };
            }

            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            const date = moment(new Date()).utc(true).format('DD-MM-YYYY');
            const marina = StorageUtil.getMarinaName();
            this.saveAsExcelFile(excelBuffer, `relatorio_unificado_servicos_${date}_${marina}`);
            this.loading = false;
          }).catch(err => {
            console.error('Erro ao preparar XLSX para exportação:', err);
            this.loading = false;
          });
        },
        error: (error) => {
          console.error('Erro ao buscar todos os dados para exportação:', error);
          this.loading = false;
        }
      });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + EXCEL_EXTENSION);
  }

  clearFilters(): void {
    this.filterForm.reset({
      customerName: '',
      codRegistration: '',
      serviceName: '',
      serviceStartDate: null,
      serviceEndDate: null,
      recurringServiceType: [] as RecurringServiceType[],
      serviceStatus: null
    });
    this.currentPage = 0;
    this.loadAllData();
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
}
